import React, { useState, useEffect } from 'react';
import Fade from 'react-reveal/Fade';
import { ImageObject, SvgImage } from '../common';
import HeroCarousel from '../HeroCarousel';
import VideoObject from '../VideoObject';
import VideoObjectProps from '../VideoObject/type';
import { Image, Link } from '../../typings';
import './styles.scss';

interface HomepageHeaderSection {
  title: string;
  link: Link;
  heroImage: Image;
  video?: VideoObjectProps;
}

export interface HomepageHeaderProps {
  headerTitle: string;
  sections: HomepageHeaderSection[];
}

const HomepageHeader = ({
  headerTitle,
  sections,
}: HomepageHeaderProps) => {
  const root = 'homepage-header';

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVideoPlaying, setVideoPlaying] = useState(false);
  const [triggeredVideo, setTriggeredVideo] = useState(false);

  useEffect(() => {
    const cycle = setInterval(() => {
      if (isVideoPlaying) {
        clearInterval(cycle);
        return;
      }
      setCurrentSlide((currentSlide + 1) % sections.length);
    }, 8000);

    return () => clearInterval(cycle);
  }, [currentSlide, isVideoPlaying]);

  useEffect(() => {
    if (!isVideoPlaying) {
      setTriggeredVideo(false);
    }
  }, [isVideoPlaying]);

  return (
    <div className={`${root} ${isVideoPlaying ? `${root}--playing-video` : ''}`}>
      <div className={`${root}__image ${isVideoPlaying ? 'no-overlay' : ''}`}>
        {sections.map((section, index) => (
          <Fade key={section.title}>
            <div className={`slide ${index === currentSlide && 'slide--visible'}`}>
              {section.video ? (
                <VideoObject
                  {...section.video}
                  thumbnailImageUrl={section.heroImage.file.url}
                  setVideoPlaying={setVideoPlaying}
                  triggeredVideo={triggeredVideo && currentSlide === index}
                />
              ) : (
                <ImageObject
                  image={section.heroImage}
                  fitType="cover"
                  useFocalPoint
                  widthSizes={['400', '800', '1600']}
                />
              )}
            </div>
          </Fade>
        ))}
        <div />
      </div>

      {!isVideoPlaying && (
        <div className={`${root}__wrapper`}>
          <div className={`${root}__top`}>
            <a className={`${root}__top__inner`} href={sections[currentSlide]?.link.url}>
              <h1 className={`${root}__top__inner__subtitle`}>
                {headerTitle}
              </h1>

              <h2 className={`${root}__top__inner__title`} aria-live="polite">
                {sections[currentSlide]?.title}
              </h2>

              {sections[currentSlide]?.video ? (
                <button
                  className={`${root}__top__inner__play-link`}
                  onClick={(e) => {
                    e.preventDefault();
                    setTriggeredVideo(true);
                  }}
                >
                  <SvgImage ariaHidden reference="#icon-play-large" />
                  Watch now
                </button>
              ) : (
                <div className={`${root}__top__inner__link`}>
                  {sections[currentSlide]?.link.title}
                  <SvgImage ariaHidden reference="#icon-long-arrow-right" />
                </div>
              )}
            </a>
          </div>
        </div>
      )}

      {!isVideoPlaying && (
        <HeroCarousel
          items={sections.map((s) => s.link)}
          mode="button"
          selectedIndex={currentSlide}
          onItemClick={(i) => setCurrentSlide(i)}
          onScrollEnd={(i) => setCurrentSlide(i)}
        />
      )}
    </div>
  );
};

export default HomepageHeader;
